// "Price from rate card" — the rep-facing half of the first caller of
// `computeDeliveredPrice` (2026-08-10).
//
// Until now a rep opened the decorator's PDF, found the row for their method,
// location, colour count and quantity, worked out a sell price, and typed two
// numbers. This reads AG Promo's loaded rate card and does it for them.
//
// ── WHY IT PREVIEWS BEFORE IT WRITES ─────────────────────────────────────────
// The preview endpoint writes nothing, so the rep sees the numbers, the
// warnings and the quantity spread BEFORE anything lands on the presentation.
// That matters most in the case where the decorator does NOT cover the job:
// the panel shows a plain refusal with the reason, instead of the rep
// discovering it after the charges are already on a client-facing document.
//
// ── THE SPREAD TABLE IS NOT DECORATION ───────────────────────────────────────
// A presentation charge is FLAT (operator-locked 2026-05-21) while the
// underlying decoration rate falls with volume. So the rep is committing to ONE
// number across every tier they quote, and the panel shows the ones they are
// not taking. Defaulting to the smallest tier means larger orders are quoted at
// or above our cost — the recoverable direction — but that is a real trade and
// the rep should be able to see it rather than have it hidden.

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Calculator, Check, Info } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  CHARGE_TYPE_LABELS,
  extractError,
} from "./PresentationDecorationSection";

// ─── Wire types (mirror the API's snake_case boundary) ───────────────────

interface RateOption {
  vendorId: string;
  vendorName: string;
  imprintTypeId: string;
  position: string;
  complexityBand: string | null;
  quantityMin: number;
  quantityMax: number | null;
}

interface ProposedCharge {
  chargeType: "run" | "setup";
  label: string;
  quantity: number;
  cost: string;
  sellPrice: string;
  displayToClient: boolean;
}

interface SpreadRow {
  quantity: number;
  decorationCostPerPiece: number | null;
  covered: boolean;
}

interface QuoteWarning {
  code: string;
  message: string;
  blocking: boolean;
}

interface DecorationQuote {
  quantity: number;
  tierQuantities: number[];
  margin: number;
  chargesStatus: "priced" | "quote_required";
  deliveredStatus: "priced" | "quote_required";
  proposedCharges: ProposedCharge[];
  decorationCostPerPiece: number;
  setupCostTotal: number;
  delivered: {
    costPerPiece: number;
    sellPerPiece: number | null;
    freightIncluded: boolean;
  } | null;
  blank: { costPerPiece: number | null; isQur: boolean; source: string | null };
  spread: SpreadRow[];
  warnings: QuoteWarning[];
}

// ─── Presentation helpers ────────────────────────────────────────────────

/** Method ids are stored as the decorator publishes them; these are the
 *  words a rep uses. An unmapped method falls back to its id rather than
 *  being hidden — a new method appearing in the rate card should show up. */
const METHOD_LABELS: Record<string, string> = {
  screen_print: "Screen print",
  embroidery: "Embroidery",
  dtf: "DTF transfer",
  vinyl: "Vinyl",
  dtg: "Direct to garment",
};

const POSITION_LABELS: Record<string, string> = {
  standard: "Standard location",
  sleeve_bag_pocket: "Sleeve / bag / pocket",
};

function methodLabel(id: string): string {
  return METHOD_LABELS[id] ?? id.replace(/_/g, " ");
}
function positionLabel(p: string): string {
  return POSITION_LABELS[p] ?? p.replace(/_/g, " ");
}

/** Complexity means something different per method, and "3c" or "10000" on
 *  its own reads as noise. */
function bandLabel(method: string, band: string | null): string {
  if (band === null) return "—";
  if (method === "screen_print") {
    const n = /^(\d+)c$/i.exec(band);
    return n ? `${n[1]} color${n[1] === "1" ? "" : "s"}` : band;
  }
  if (method === "embroidery") {
    const n = Number(band);
    return Number.isFinite(n) ? `${n.toLocaleString()} stitches` : band;
  }
  // dtf / vinyl publish a print SIZE in inches.
  return `${band}"`;
}

function money(v: number | string): string {
  return `$${Number(v).toFixed(2)}`;
}

/** The margin this charge will show once committed. Derived the same way the
 *  DB's generated `margin_pct` column derives it — `(sell - cost) / sell` — so
 *  the preview cannot disagree with the row the rep sees after applying. */
function marginPct(c: ProposedCharge): string {
  const sell = Number(c.sellPrice);
  if (!Number.isFinite(sell) || sell === 0) return "—";
  return `${(((sell - Number(c.cost)) / sell) * 100).toFixed(1)}%`;
}

/** The uplifts AG publishes, and the only methods each applies to. Mirrors
 *  `UPLIFTS` in delivered-price.ts — the server ignores (and warns about) an
 *  uplift sent for the wrong method, so this only keeps the UI honest. */
const UPLIFTS = [
  { key: "dark_garment", label: "Dark garment (flash) +14%", methods: ["screen_print"] },
  { key: "pms_match", label: "PMS color match +15%", methods: ["screen_print"] },
  { key: "puff", label: "Puff embroidery +$1.50", methods: ["embroidery"] },
  { key: "stabiliser", label: "Stabiliser (fleece/knit) +$0.75", methods: ["embroidery"] },
] as const;

interface Props {
  itemId: string;
  /** The item's tier quantities, ascending. Empty means the item has no
   *  pricing yet, which the panel refuses on rather than guessing. */
  tierQuantities: number[];
  /** Whether the item already carries charges — drives the replace prompt. */
  hasExistingCharges: boolean;
  onApplied: () => void;
  onCancel: () => void;
}

export function DecorationRateCardPanel(props: Props) {
  const { itemId, tierQuantities, hasExistingCharges, onApplied, onCancel } =
    props;

  const options = useQuery<{ options: RateOption[] }>({
    queryKey: ["decoration-rate-options"],
    queryFn: async () => api("/decoration_rate_options"),
  });

  const rows = options.data?.options ?? [];

  const [vendorId, setVendorId] = useState<string>("");
  const [method, setMethod] = useState<string>("");
  const [position, setPosition] = useState<string>("");
  const [band, setBand] = useState<string>("");
  const [quantity, setQuantity] = useState<number | null>(null);
  const [isReorder, setIsReorder] = useState(false);
  const [uplifts, setUplifts] = useState<Record<string, boolean>>({});
  const [replace, setReplace] = useState(false);
  const [quote, setQuote] = useState<DecorationQuote | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cascading option lists — each level is filtered by the one above, so a rep
  // can only build a key the rate card actually contains. A free-text field
  // here would fail as "no rate covers this", which is indistinguishable from
  // a genuine gap in the decorator's sheet.
  const vendors = useMemo(
    () =>
      [...new Map(rows.map((r) => [r.vendorId, r.vendorName])).entries()].map(
        ([id, name]) => ({ id, name }),
      ),
    [rows],
  );
  const methods = useMemo(
    () => [...new Set(rows.filter((r) => r.vendorId === vendorId).map((r) => r.imprintTypeId))],
    [rows, vendorId],
  );
  const positions = useMemo(
    () => [
      ...new Set(
        rows
          .filter((r) => r.vendorId === vendorId && r.imprintTypeId === method)
          .map((r) => r.position),
      ),
    ],
    [rows, vendorId, method],
  );
  const bands = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            r.vendorId === vendorId &&
            r.imprintTypeId === method &&
            r.position === position,
        )
        .map((r) => r.complexityBand),
    [rows, vendorId, method, position],
  );

  // Default to the only choice at each level — with one decorator and one
  // position per method, making the rep click through four identical dropdowns
  // is friction with no decision in it.
  const effVendor = vendorId || (vendors.length === 1 ? vendors[0]!.id : "");
  const effMethod = method || (methods.length === 1 ? methods[0]! : "");
  const effPosition = position || (positions.length === 1 ? positions[0]! : "");
  const effQuantity = quantity ?? tierQuantities[0] ?? null;

  const selectedKey =
    effVendor && effMethod && effPosition && (band !== "" || bands.includes(null));

  const applicableUplifts = UPLIFTS.filter((u) =>
    (u.methods as readonly string[]).includes(effMethod),
  );

  function placementBody() {
    return {
      placements: [
        {
          vendor_id: effVendor,
          imprint_type_id: effMethod,
          position: effPosition,
          complexity_band: band === "" ? null : band,
          is_reorder: isReorder || undefined,
          dark_garment: uplifts.dark_garment || undefined,
          pms_match: uplifts.pms_match || undefined,
          puff: uplifts.puff || undefined,
          stabiliser: uplifts.stabiliser || undefined,
        },
      ],
      quantity: effQuantity ?? undefined,
    };
  }

  const preview = useMutation({
    mutationFn: async () =>
      api<{ quote: DecorationQuote }>(
        `/presentation_items/${itemId}/decoration_quote`,
        { method: "POST", body: JSON.stringify(placementBody()) },
      ),
    onError: (err) => {
      setQuote(null);
      setError(extractError(err));
    },
    onSuccess: (data) => {
      setError(null);
      setQuote(data.quote);
    },
  });

  const apply = useMutation({
    mutationFn: async () =>
      api(`/presentation_items/${itemId}/decoration_charges`, {
        method: "POST",
        body: JSON.stringify({ ...placementBody(), replace: replace || undefined }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => onApplied(),
  });

  if (tierQuantities.length === 0) {
    return (
      <div className="mt-3 rounded-md border border-peach-300/70 bg-white p-3">
        <p className="text-xs text-brown-400">
          Add pricing tiers first — decoration is priced at a quantity, and
          without tiers there is no quantity to price at.
        </p>
        <div className="mt-3 flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // 🔴 The backend keeps TWO verdicts apart on purpose (see "trap 3" in
  // presentation-item-decoration-pricing.ts): `chargesStatus` is blocked only
  // by a missing DECORATION rate, while `deliveredStatus` is additionally
  // blocked by a missing or QUR blank cost. The charges do not depend on the
  // blank at all.
  //
  // Reading `warnings.some(blocking)` here would collapse that split and hide
  // a perfectly good price behind a red "Blocked" card whenever the garment
  // happens to have no cost bracket — which today is the COMMON case, not an
  // edge one. Only `chargesStatus` may suppress the table.
  const chargesRefused = quote !== null && quote.chargesStatus !== "priced";
  const chargeBlockers =
    quote?.warnings.filter(
      (w) => w.blocking && w.code === "DECORATION_QUOTE_REQUIRED",
    ) ?? [];
  /** Blocking, but only for the DELIVERED figure — the charges are still good. */
  const deliveredBlockers =
    quote?.warnings.filter(
      (w) => w.blocking && w.code !== "DECORATION_QUOTE_REQUIRED",
    ) ?? [];
  const advisories = quote?.warnings.filter((w) => !w.blocking) ?? [];
  const canApply = quote !== null && quote.chargesStatus === "priced";

  return (
    <div className="mt-3 rounded-md border border-peach-300/70 bg-white p-3">
      <div className="mb-3 flex items-baseline gap-2">
        <Calculator className="h-3.5 w-3.5 text-brown-300" />
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Price from the decorator's rate card
        </p>
      </div>

      {options.isLoading && (
        <p className="text-xs italic text-brown-300">Loading rate cards…</p>
      )}
      {options.isSuccess && rows.length === 0 && (
        <p className="text-xs text-rust-600">
          No decorator rate card is loaded yet, so there is nothing to price
          from. Load one before using this.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
            <Picker
              label="Decorator"
              value={effVendor}
              onChange={(v) => {
                setVendorId(v);
                setMethod("");
                setPosition("");
                setBand("");
                setQuote(null);
              }}
              options={vendors.map((v) => ({ value: v.id, label: v.name }))}
            />
            <Picker
              label="Method"
              value={effMethod}
              disabled={!effVendor}
              onChange={(v) => {
                setMethod(v);
                setPosition("");
                setBand("");
                setUplifts({});
                setQuote(null);
              }}
              options={methods.map((m) => ({ value: m, label: methodLabel(m) }))}
            />
            <Picker
              label="Location"
              value={effPosition}
              disabled={!effMethod}
              onChange={(v) => {
                setPosition(v);
                setBand("");
                setQuote(null);
              }}
              options={positions.map((p) => ({
                value: p,
                label: positionLabel(p),
              }))}
            />
            <Picker
              label={
                effMethod === "screen_print"
                  ? "Colors"
                  : effMethod === "embroidery"
                    ? "Stitches"
                    : "Size"
              }
              value={band}
              disabled={!effPosition}
              onChange={(v) => {
                setBand(v);
                setQuote(null);
              }}
              // A dimensionless method (laser engraving has no colour count)
              // stores NULL, which maps to the same empty value as the
              // Picker's own "—" placeholder. Two identical blank rows in one
              // select reads as a glitch, so the null band is labelled.
              options={bands.map((b) => ({
                value: b ?? "",
                label: b === null ? "Not applicable" : bandLabel(effMethod, b),
              }))}
            />
            <Picker
              label="Priced at"
              wide
              value={String(effQuantity ?? "")}
              onChange={(v) => {
                setQuantity(Number(v));
                setQuote(null);
              }}
              options={tierQuantities.map((q) => ({
                value: String(q),
                label: `${q.toLocaleString()} pcs`,
              }))}
            />
          </div>

          {/* Stated up front, not buried in the results. This is the single
              most business-critical piece of context in the panel: the charge
              is FLAT while the decorator's rate falls with volume, so which
              quantity it is priced at decides whether larger orders are quoted
              above or below our cost. */}
          <p className="mt-1 text-[10px] text-brown-300">
            Defaults to your smallest tier — the safe direction, because a charge
            is one flat number across every quantity you quote.
          </p>

          {(applicableUplifts.length > 0 || effMethod !== "") && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Toggle
                label="Artwork already on file (reorder)"
                checked={isReorder}
                onChange={(v) => {
                  setIsReorder(v);
                  setQuote(null);
                }}
              />
              {applicableUplifts.map((u) => (
                <Toggle
                  key={u.key}
                  label={u.label}
                  checked={uplifts[u.key] ?? false}
                  onChange={(v) => {
                    setUplifts((s) => ({ ...s, [u.key]: v }));
                    setQuote(null);
                  }}
                />
              ))}
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!selectedKey || preview.isPending}
              onClick={() => preview.mutate()}
            >
              {preview.isPending ? "Pricing…" : "Show the price"}
            </Button>
          </div>
        </>
      )}

      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}

      {quote && (
        <div className="mt-3 border-t border-brown-50 pt-3">
          {chargesRefused ? (
            <div className="rounded-md border border-rust-300 bg-rust-50 p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-rust-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                The decorator does not publish a rate for this
              </p>
              {chargeBlockers.map((w) => (
                <p key={w.code} className="mt-1 text-xs text-brown-500">
                  {w.message}
                </p>
              ))}
              <p className="mt-1 text-xs text-brown-400">
                Ask them for a quote — a missing rate is not a free one, so
                nothing has been added.
              </p>
            </div>
          ) : (
            <>
              {/* Column-for-column with the committed charges table in
                  PresentationItemChargesSection — a rep previews one shape and
                  then sees the same shape one scroll away after applying. */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
                    <tr>
                      <th className="py-1 pr-2">Type</th>
                      <th className="py-1 pr-2">Label</th>
                      <th className="py-1 pr-2">Qty</th>
                      <th className="py-1 pr-2">Cost</th>
                      <th className="py-1 pr-2">Sell</th>
                      <th className="py-1 pr-2">Margin</th>
                      <th className="py-1 pr-2">Total sell</th>
                      <th className="py-1 pr-2">Visible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.proposedCharges.map((c, i) => (
                      <tr key={i} className="border-t border-brown-50/60">
                        <td className="py-1.5 pr-2">
                          <span className="rounded-full bg-peach-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500">
                            {CHARGE_TYPE_LABELS[c.chargeType] ?? c.chargeType}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 text-brown-500">{c.label}</td>
                        <td className="py-1.5 pr-2 text-brown-400">
                          {c.quantity.toLocaleString()}
                        </td>
                        <td className="py-1.5 pr-2 text-brown-400">
                          {money(c.cost)}
                        </td>
                        <td className="py-1.5 pr-2 font-semibold text-brown-500">
                          {money(c.sellPrice)}
                        </td>
                        <td className="py-1.5 pr-2 text-brown-400">
                          {marginPct(c)}
                        </td>
                        <td className="py-1.5 pr-2 font-semibold text-rust-500">
                          {money(Number(c.sellPrice) * c.quantity)}
                        </td>
                        <td className="py-1.5 pr-2">
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                              c.displayToClient
                                ? "bg-peach-100 text-brown-500"
                                : "border border-rust-300 text-rust-500",
                            )}
                          >
                            {c.displayToClient ? "Visible" : "Internal only"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="mt-2 text-[11px] text-brown-400">
                Priced at {quote.quantity.toLocaleString()} pieces, at the{" "}
                {(quote.margin * 100).toFixed(0)}% margin this item's{" "}
                {quote.quantity.toLocaleString()}-piece tier already uses.{" "}
                <strong className="font-semibold text-brown-500">
                  The garment is not included
                </strong>{" "}
                — it is already in the tier price.
              </p>

              {/* The full delivered picture, when the garment's cost is known.
                  Separate from the charges on purpose: these charges are what
                  gets written, this is what the job actually costs. */}
              {quote.delivered !== null && (
                <p className="mt-1 text-[11px] text-brown-400">
                  All in, this job costs{" "}
                  <strong className="font-semibold text-brown-500">
                    {money(quote.delivered.costPerPiece)}/pc
                  </strong>{" "}
                  (garment + decoration + setup)
                  {quote.delivered.sellPerPiece !== null && (
                    <>
                      {" "}
                      and sells at {money(quote.delivered.sellPerPiece)}/pc
                    </>
                  )}
                  {!quote.delivered.freightIncluded &&
                    " — before freight, which is not estimated yet"}
                  .
                </p>
              )}

              {/* Blocking for the DELIVERED figure only. The charges above are
                  good; saying "Blocked" here would be false. */}
              {deliveredBlockers.length > 0 && (
                <div className="mt-2 rounded-md border border-peach-300 bg-peach-50 p-2">
                  <p className="text-[11px] font-semibold text-brown-500">
                    The charges above are ready. The full delivered price is not.
                  </p>
                  {deliveredBlockers.map((w) => (
                    <p key={w.code} className="mt-0.5 text-[11px] text-brown-400">
                      {w.message}
                    </p>
                  ))}
                </div>
              )}

              {/* The flat-charge trade, stated rather than hidden. */}
              {quote.spread.length > 1 && (
                <div className="mt-2 rounded-md bg-cream-50 p-2">
                  <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                    <Info className="h-3 w-3" />
                    The same decoration across your tiers (your selection in bold)
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                    {quote.spread.map((s) => (
                      <span
                        key={s.quantity}
                        className={cn(
                          "text-brown-400",
                          s.quantity === quote.quantity &&
                            "font-semibold text-brown-500",
                        )}
                      >
                        {s.quantity.toLocaleString()} pcs:{" "}
                        {s.covered && s.decorationCostPerPiece !== null
                          ? `${money(s.decorationCostPerPiece)}/pc`
                          : "not covered"}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-[10px] text-brown-300">
                    A charge is one flat number across every tier. Pricing at
                    your smallest quantity keeps larger orders at or above cost.
                  </p>
                </div>
              )}
            </>
          )}

          {advisories.length > 0 && (
            <div className="mt-2 space-y-1">
              {advisories.map((w) => (
                <p
                  key={w.code}
                  className="flex items-start gap-1.5 text-[11px] text-brown-400"
                >
                  <Info className="mt-0.5 h-3 w-3 shrink-0 text-brown-300" />
                  <span>{w.message}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {canApply && hasExistingCharges && (
        <div className="mt-3">
          <Toggle
            label="Replace the charges already on this item"
            checked={replace}
            onChange={setReplace}
          />
          {!replace && (
            <p className="mt-1 text-[11px] text-rust-600">
              This item already has charges. Tick the box to replace them, or
              they would be duplicated.
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={apply.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={
            !canApply ||
            apply.isPending ||
            (hasExistingCharges && !replace)
          }
          onClick={() => apply.mutate()}
        >
          {apply.isPending ? (
            "Adding…"
          ) : (
            <>
              <Check className="mr-1 h-3.5 w-3.5" />
              Add these charges
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Small controls ──────────────────────────────────────────────────────

function Picker(props: {
  label: string;
  value: string;
  disabled?: boolean;
  /** Span both columns at the narrow breakpoint. Five pickers in a 2-column
   *  grid leaves the fifth orphaned at half width. */
  wide?: boolean;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className={cn("block", props.wide && "col-span-2 md:col-span-1")}>
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </span>
      <select
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
        className="h-8 w-full rounded-md border border-brown-100 bg-white px-2 text-xs text-brown-500 focus:border-peach-400 focus:outline-none disabled:bg-cream-50 disabled:text-brown-300"
      >
        <option value="">—</option>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Toggle(props: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] text-brown-500">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
        // Matches the codebase's other checkbox (ClientOverviewTab) rather
        // than inventing a second size + focus treatment.
        className="h-4 w-4 rounded border-brown-200 text-rust-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50"
      />
      {props.label}
    </label>
  );
}
